// Cards, card grids and horizontal carousels (Swift Card / CardHGrid / CardVGrid).
import React from 'react';
import { FlatList, Pressable, Text, View, useWindowDimensions } from 'react-native';
import Photo from './Photo';
import Icon, { type IconName } from './Icon';
import DietMark from './DietMark';
import { font, radius, spacing, usePalette } from '../theme';
import type { MediaRef } from '../types';
import type { DietFlags } from '../logic/diets';

export type CardData = {
  id: string;
  title: string;
  subtitle?: string;
  media?: MediaRef;
  icon?: IconName;
  badge?: { icon: IconName; color: string };
  /** Diet marks (red: certainly breaks, orange: unconfirmed warning); the title turns secondary. */
  diets?: DietFlags;
};

export function Card({
  item, onPress, onLongPress, selected, width,
}: { item: CardData; onPress: () => void; onLongPress?: () => void; selected?: boolean; width?: number }) {
  const p = usePalette();
  const muted = !!item.diets && (item.diets.breaks.length > 0 || item.diets.unsure.length > 0);
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={item.title}
      accessibilityState={selected !== undefined ? { selected } : undefined}
      onPress={onPress}
      onLongPress={onLongPress}
      style={({ pressed }) => ({ width, gap: spacing.s, opacity: pressed ? 0.75 : 1 })}>
      <View>
        <Photo media={item.media} seed={item.id} icon={item.icon} style={{ width: '100%', aspectRatio: 1 }} />
        {item.badge ? (
          <View style={{ position: 'absolute', top: spacing.s, right: spacing.s, backgroundColor: p.background, borderRadius: radius.pill, padding: 4 }}>
            <Icon name={item.badge.icon} size={16} color={item.badge.color} />
          </View>
        ) : null}
        {item.diets && (item.diets.breaks.length || item.diets.unsure.length) ? (
          <View style={{ position: 'absolute', bottom: spacing.s, left: spacing.s }}>
            <DietMark flags={item.diets} />
          </View>
        ) : null}
        {selected !== undefined ? (
          <View style={{ position: 'absolute', top: spacing.s, left: spacing.s, backgroundColor: p.background, borderRadius: radius.pill }}>
            <Icon name={selected ? 'checkCircle' : 'circle'} color={selected ? p.success : p.secondaryText} />
          </View>
        ) : null}
      </View>
      <Text style={{ fontSize: font.body, fontWeight: muted ? '400' : '600', color: muted ? p.secondaryText : p.text }} numberOfLines={2}>{item.title}</Text>
      {item.subtitle ? <Text style={{ fontSize: font.footnote, color: p.secondaryText }} numberOfLines={1}>{item.subtitle}</Text> : null}
    </Pressable>
  );
}

/** Two columns on phones, more on wide screens. Rendered inline (pages scroll as a whole). */
export function CardGrid({
  items, onPress, onLongPress, isSelected,
}: { items: CardData[]; onPress: (id: string) => void; onLongPress?: (id: string) => void; isSelected?: (id: string) => boolean | undefined }) {
  const { width } = useWindowDimensions();
  const columns = width >= 1000 ? 5 : width >= 700 ? 4 : 2;
  const inner = Math.min(width, 1200) - spacing.l * 2;
  const cardWidth = (inner - spacing.m * (columns - 1)) / columns;
  return (
    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: spacing.m }}>
      {items.map(item => (
        <Card key={item.id} item={item} width={cardWidth} selected={isSelected?.(item.id)} onPress={() => onPress(item.id)} onLongPress={onLongPress ? () => onLongPress(item.id) : undefined} />
      ))}
    </View>
  );
}

/** About 2.5 cards visible, snapping (Swift CardHGrid). */
export function Carousel({ items, onPress, onLongPress, isSelected }: { items: CardData[]; onPress: (id: string) => void; onLongPress?: (id: string) => void; isSelected?: (id: string) => boolean | undefined }) {
  const { width } = useWindowDimensions();
  const cardWidth = Math.min(220, (Math.min(width, 1200) - spacing.l * 2) / 2.5);
  return (
    <FlatList
      horizontal
      data={items}
      keyExtractor={i => i.id}
      showsHorizontalScrollIndicator={false}
      snapToInterval={cardWidth + spacing.m}
      decelerationRate="fast"
      contentContainerStyle={{ gap: spacing.m }}
      style={{ marginHorizontal: -spacing.l }}
      ListHeaderComponent={<View style={{ width: spacing.l - spacing.m }} />}
      ListFooterComponent={<View style={{ width: spacing.l - spacing.m }} />}
      renderItem={({ item }) => (
        <Card item={item} width={cardWidth} selected={isSelected?.(item.id)} onPress={() => onPress(item.id)} onLongPress={onLongPress ? () => onLongPress(item.id) : undefined} />
      )}
    />
  );
}
